import { EMPTY, fromEvent, merge, tap } from 'rxjs';

const mainPageContent = document.querySelector('[data-layout-container]');
const mapButtonToggle = document.querySelector('[data-map-button-toggle]');
const listButtonToggle = document.querySelector('[data-list-button-toggle]');
/**
 * This function adds specific classes to the container element to set it to a map view
 * layout.
 */
const setMapView = () => mainPageContent?.classList.add('nhs-c-map--is-active');

/**
 * This function removes specific classes from a container element and dispatches a custom
 * event to toggle the list view.
 */
const setListView = () => mainPageContent?.classList.remove('nhs-c-map--is-active');

/**
 * Hides the map view toggle button.
 */
const hideMapButton = () => mapButtonToggle?.setAttribute('hidden', '');

/**
 * Hides the list view toggle button.
 */
const hideListButton = () => listButtonToggle?.setAttribute('hidden', '');

/**
 * Shows the map view toggle button.
 */
const showMapButton = () => mapButtonToggle?.removeAttribute('hidden');

/**
 * Shows the list view toggle button.
 */
const showListButton = () => listButtonToggle?.removeAttribute('hidden');

/**
 * Dispatches a custom 'listViewToggled' event on the document body.
 * This event notifies other components (e.g., map boundaries) that the user has switched to list view.
 */
const dispatchListViewEvent = () => {
  const event = new CustomEvent('listViewToggled');
  document.body.dispatchEvent(event);
};

/**
 * Dispatches a custom 'mapViewToggled' event on the document body.
 * This event notifies other components (e.g., map boundaries) that the user has switched to map view.
 */
const dispatchMapViewEvent = () => {
  const event = new CustomEvent('mapViewToggled');
  document.body.dispatchEvent(event);
};


/**
 * Initialize button visibility on page load.
 * 
 * - If the map button has the 'hidden' attribute, we're in map view mode.
 *   Show the list button and hide the map button.
 * - If the map button is visible, we're in list view mode.
 *   Hide the list button and show the map button.
 * 
 * This handles the case where MapButton.cshtml has conditional hidden attribute
 * but ListViewButton.cshtml doesn't (since it's rendered in a different model context).
 */
function initializeButtonVisibility() {
  // Check if mapButtonToggle has the hidden attribute
  const isMapViewActive = mapButtonToggle?.hasAttribute('hidden');
  
  if (isMapViewActive) {
    // Map view is active - show list button, hide map button
    showListButton();
    hideMapButton();
    setMapView();
    dispatchMapViewEvent();
    
  } else {
    // List view is active (default) - hide list button, show map button
    hideListButton();
    showMapButton();
    setListView();
    dispatchListViewEvent();
  }
}

// Initialize button visibility when the script loads
initializeButtonVisibility();

/**
 * An observable stream that listens for click events on the `listButtonToggle` element.
 * When a click event occurs, it maps the event to the current target
 * element, then sequentially performs the following actions using the `tap` operator:
 * 1. `hideListButton`: Hides the list view toggle button.
 * 2. `showMapButton`: Shows the map view toggle button.
 * 3. `setListView`: Removes specific classes from a container element and dispatches a custom event to
 * toggle the list view.
 * state.
 */
const onListButtonClick$ = listButtonToggle
  ? fromEvent(listButtonToggle, 'click').pipe(
      tap(hideListButton),
      tap(showMapButton),
      tap(setListView),
      tap(dispatchListViewEvent),
    )
  : EMPTY;

/**
 * An observable stream that listens for click events on the `mapButtonToggle` element.
 * When a click event occurs, it maps the event to the current target
 * element, then sequentially performs the following actions using the `tap` operator:
 * 1. `hideMapButton`: Hides the map view toggle button.
 * 2. `showListButton`: Shows the list view toggle button.
 * 3. `setMapView`: Sets specific classes from a container element.
 * state.
 */
const onMapButtonClick$ = mapButtonToggle
  ? fromEvent(mapButtonToggle, 'click').pipe(
      tap(hideMapButton),
      tap(showListButton),
      tap(setMapView),
      tap(dispatchMapViewEvent),
    )
  : EMPTY;

merge(onListButtonClick$, onMapButtonClick$).subscribe();
